// src/context/CartContext.jsx
import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import { productService } from "../services/productService";
import { db } from "../firebase";
import { doc, getDoc, setDoc, onSnapshot } from "firebase/firestore";
import { useAuth } from "./AuthContext";

const CartContext = createContext();
const GUEST_CART_KEY = "cart";

// 🔥 YENİ: Boş metin ("") ile null değerini aynı kabul eden akıllı eşleştirici
const normalizeAttr = (val) => {
  if (val === null || val === undefined || String(val).trim() === "") return null;
  return String(val).trim();
};

// Misafir sepeti ile hesap sepetini birleştirir (id+renk+beden eşleşirse adetleri toplar)
function mergeCartItems(accountItems = [], guestItems = []) {
  const merged = [...accountItems];

  guestItems.forEach((guestItem) => {
    const idx = merged.findIndex(
      (it) =>
        it.id === guestItem.id &&
        normalizeAttr(it.selectedColor) === normalizeAttr(guestItem.selectedColor) &&
        normalizeAttr(it.selectedSize) === normalizeAttr(guestItem.selectedSize)
    );

    if (idx !== -1) {
      merged[idx] = {
        ...merged[idx],
        quantity: Number(merged[idx].quantity || 0) + Number(guestItem.quantity || 0),
      };
    } else {
      merged.push(guestItem);
    }
  });

  return merged;
}

export function CartProvider({ children }) {
  const { currentUser } = useAuth();

  const [cartItems, setCartItems] = useState(() => {
    try {
      const storedCart = localStorage.getItem(GUEST_CART_KEY);
      return storedCart ? JSON.parse(storedCart) : [];
    } catch (e) {
      console.warn("Sepet verisi okunamadı, sıfırlanıyor:", e);
      return [];
    }
  });

  const [cartOpen, setCartOpen] = useState(false);
  const [shippingSettings, setShippingSettings] = useState({
    cost: 0,
    freeShippingThreshold: 0,
  });

  // Hangi kullanıcı için senkron olduğumuzu takip eder (login/logout geçişlerini yakalamak için)
  const syncedUidRef = useRef(null);

  // --- Kargo Ayarlarını Dinle ---
  useEffect(() => {
    const settingsRef = doc(db, "settings", "shipping");
    const unsubscribe = onSnapshot(settingsRef, (docSnap) => {
      if (docSnap.exists()) {
        setShippingSettings(docSnap.data());
      } else {
        setShippingSettings({ cost: 0, freeShippingThreshold: 0 });
      }
    });
    return () => unsubscribe();
  }, []);

  // --- LOGIN / LOGOUT GEÇİŞLERİNİ YÖNET ---
  useEffect(() => {
    if (!currentUser) {
      syncedUidRef.current = null;
      try {
        const storedGuestCart = localStorage.getItem(GUEST_CART_KEY);
        setCartItems(storedGuestCart ? JSON.parse(storedGuestCart) : []);
      } catch {
        setCartItems([]);
      }
      return;
    }

    if (syncedUidRef.current === currentUser.uid) return; 

    let cancelled = false;
    (async () => {
      try {
        const guestCartRaw = localStorage.getItem(GUEST_CART_KEY);
        const guestCart = guestCartRaw ? JSON.parse(guestCartRaw) : [];

        const ref = doc(db, "carts", currentUser.uid);
        const snap = await getDoc(ref);
        const accountCart = snap.exists() && Array.isArray(snap.data().items) ? snap.data().items : [];

        const merged = mergeCartItems(accountCart, guestCart);

        try {
          await setDoc(ref, { items: merged, updatedAt: Date.now() }, { merge: true });
          localStorage.removeItem(GUEST_CART_KEY);
        } catch (writeErr) {
          console.error("Sepet Firestore'a yazılamadı, misafir veri korunuyor:", writeErr);
        }

        if (cancelled) return;

        syncedUidRef.current = currentUser.uid;
        setCartItems(merged);
      } catch (e) {
        console.error("Sepet senkronizasyonu başarısız:", e);
        syncedUidRef.current = currentUser.uid;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  // --- Üye girişliyken: her değişiklik Firestore'a yazılır ---
  useEffect(() => {
    if (!currentUser || syncedUidRef.current !== currentUser.uid) return;
    const ref = doc(db, "carts", currentUser.uid);
    setDoc(ref, { items: cartItems, updatedAt: Date.now() }, { merge: true }).catch((e) =>
      console.warn("Sepet Firestore'a yazılamadı:", e)
    );
  }, [cartItems, currentUser]);

  // --- Misafirken: her değişiklik localStorage'a yazılır ---
  useEffect(() => {
    if (currentUser) return;
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartItems));
    } catch (e) {
      console.warn("Sepet kaydedilemedi:", e);
    }
  }, [cartItems, currentUser]);

  // 3. Sepetteki toplam ürün sayısı
  const cartCount = useMemo(() => {
    return (cartItems || []).reduce((sum, it) => sum + (Number(it.quantity) || 0), 0);
  }, [cartItems]);

  const normalizedPrice = (p) => {
    if (typeof p === "number") return p;
    const num = Number(String(p || "0").replace(/[^\d.]/g, ""));
    return Number.isFinite(num) ? num : 0;
  };

  const addToCart = async (product = {}) => {
    const qtyToAdd = Math.max(1, Number(product.quantity || 1));
    const id = product.id;
    
    // 🔥 YENİ: Metinleri normalize et (null ile "" aynı olacak)
    const color = normalizeAttr(product.selectedColor);
    const size = normalizeAttr(product.selectedSize);

    if (!id) return { success: false, message: "Ürün bilgisi eksik." };

    let passedStock = typeof product.availableStock === "number" ? Number(product.availableStock) : null;
    let currentStockLimit = passedStock;

    if (currentStockLimit === null) {
      try {
        const stockFromService = await productService.getProductStock(id, size);
        currentStockLimit = typeof stockFromService === "number" ? Number(stockFromService) : 0;
      } catch (e) {
        console.warn("Stok bilgisi alınamadı:", e);
        currentStockLimit = 0;
      }
    }

    return new Promise((resolve) => {
      let opResult = { success: false, message: "İşlem yapılamadı." };

      setCartItems((prevItems) => {
        const existingIndex = prevItems.findIndex(
          (item) =>
            item.id === id &&
            normalizeAttr(item.selectedColor) === color &&
            normalizeAttr(item.selectedSize) === size
        );

        if (existingIndex !== -1) {
          const updated = [...prevItems];
          const existingItem = updated[existingIndex];
          const desiredQty = Number(existingItem.quantity || 0) + qtyToAdd;

          if (desiredQty > currentStockLimit) {
            if (existingItem.quantity >= currentStockLimit) {
              opResult = { success: false, message: "Stok yetersiz." };
              return prevItems;
            }
            updated[existingIndex] = { ...existingItem, quantity: currentStockLimit };
            opResult = { success: true, message: "Stoktaki son ürünler eklendi." };
            return updated;
          }

          updated[existingIndex] = { ...existingItem, quantity: desiredQty };
          opResult = { success: true, message: "Ürün sepete eklendi." };
          return updated;
        } else {
          const initialQty = Math.min(qtyToAdd, currentStockLimit);

          if (initialQty <= 0) {
            opResult = { success: false, message: "Ürün stokta yok." };
            return prevItems;
          }

          const effectivePrice = (product.discountPrice && Number(product.discountPrice) > 0)
            ? Number(product.discountPrice)
            : Number(product.price);

          const newItem = {
            id,
            name: product.name || "",
            price: normalizedPrice(effectivePrice),
            originalPrice: normalizedPrice(product.price),
            quantity: initialQty,
            selectedColor: color, // Normalize hali kaydedilir
            selectedSize: size,   // Normalize hali kaydedilir
            image: product.image || null,
            variantGroupId: product.variantGroupId || null,
          };

          if (initialQty < qtyToAdd) {
            opResult = { success: true, message: `Stok yetersiz, sadece ${initialQty} adet eklendi.` };
          } else {
            opResult = { success: true, message: "Ürün sepete eklendi." };
          }

          return [...prevItems, newItem];
        }
      });

      resolve(opResult);
    });
  };

  const updateQuantity = (id, selectedColor, selectedSize, newQuantity, availableStock) => {
    const desired = Number(newQuantity || 0);
    const color = normalizeAttr(selectedColor);
    const size = normalizeAttr(selectedSize);

    if (!id) return { success: false, message: "Ürün ID eksik." };

    setCartItems((prevItems) => {
      if (desired <= 0) {
        return prevItems.filter(
          (it) =>
            !(
              it.id === id &&
              normalizeAttr(it.selectedColor) === color &&
              normalizeAttr(it.selectedSize) === size
            )
        );
      }

      if (typeof availableStock === "number" && desired > availableStock) {
        return prevItems.map((it) =>
          it.id === id &&
          normalizeAttr(it.selectedColor) === color &&
          normalizeAttr(it.selectedSize) === size
            ? { ...it, quantity: availableStock }
            : it
        );
      }

      return prevItems.map((it) =>
        it.id === id &&
        normalizeAttr(it.selectedColor) === color &&
        normalizeAttr(it.selectedSize) === size
          ? { ...it, quantity: desired }
          : it
      );
    });

    return { success: true };
  };

  const removeFromCart = (id, selectedColor, selectedSize) => {
    const color = normalizeAttr(selectedColor);
    const size = normalizeAttr(selectedSize);

    setCartItems((prevItems) =>
      prevItems.filter(
        (item) =>
          !(
            item.id === id &&
            normalizeAttr(item.selectedColor) === color &&
            normalizeAttr(item.selectedSize) === size
          )
      )
    );
  };

  const clearCart = async () => {
    setCartItems([]);
    localStorage.removeItem(GUEST_CART_KEY);
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify([]));

    if (currentUser) {
      try {
        const ref = doc(db, "carts", currentUser.uid);
        await setDoc(ref, { items: [], updatedAt: Date.now() }, { merge: true });
      } catch (e) {
        console.warn("Veritabanı sepeti temizlenemedi:", e);
      }
    }
  };

  const openCart = () => setCartOpen(true);
  const closeCart = () => setCartOpen(false);
  const toggleCart = () => setCartOpen((s) => !s);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        cartCount,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        cartOpen,
        openCart,
        closeCart,
        toggleCart,
        shippingSettings,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    if (typeof window !== "undefined" && process.env.NODE_ENV !== "production") {
      console.warn("useCart must be used within a CartProvider");
    }
    return null;
  }
  return context;
}